import { useMemo } from 'react'
import useSWR from 'swr'
import { useAccount } from 'wagmi'
import { useV6SDK, useDebounce } from '~/hooks'
import { useTokenlonV6NetworkConfig } from '~/state/dex/hooks'
import {
  useSlippage,
  useGasPrice,
  useGasLimitScale,
} from '~/components/Exchange/hooks'
import { getRateOrOrder } from '~/apis/exchange'
import { Mainnet } from '~/model/chain/ethereum'
import { ETH } from '~/constants/tokens'
import { DEFAULT_GAS_LIMIT } from '~/constants/unit'
import { formatEther, formatUnits, zeroAddress } from 'viem'
import BigNumber from 'bignumber.js'
import { useTokenPrice } from '~/hooks/useTokenPrice'

export const useTokenlonWithdrawalQuote = (
  token: ERC20Token,
  amount: string,
) => {
  const { address: account } = useAccount()
  const sdk = useV6SDK()
  const debouncedAmount = useDebounce(amount, 500)
  const inputToken = { ...token, chainId: Mainnet.chainId } as SpotSwapToken
  const outputToken = { ...ETH, chainId: Mainnet.chainId } as SpotSwapToken
  const isSendByRelayer = false
  const { slippageBPS } = useSlippage()
  const { quoteGasPrice } = useGasPrice()
  const {
    defaultGasLimit: networkDefaultGasLimit,
  } = useTokenlonV6NetworkConfig()

  const defaultGasLimit = useMemo(() => {
    return BigInt(networkDefaultGasLimit || DEFAULT_GAS_LIMIT)
  }, [networkDefaultGasLimit])
  const gasLimitScale = useGasLimitScale()
  const ethPrice = useTokenPrice(zeroAddress)

  const { data, isValidating } = useSWR(
    [
      'lido-withdrawal-tokenlon-quote',
      inputToken,
      outputToken,
      account,
      debouncedAmount,
      slippageBPS,
      isSendByRelayer,
    ],
    () =>
      getRateOrOrder({
        sdk,
        inputToken,
        outputToken,
        account,
        amount: debouncedAmount || '1',
        slippageBPS,
        isSendByRelayer,
        gasPrice: quoteGasPrice,
        defaultGasLimit,
        gasLimitScale,
        recipient: account,
      }),
    { refreshInterval: 60000 },
  )

  const orderInfo = data?.usefulOrderInfo

  const rate = orderInfo?.rate ? orderInfo.rate : undefined
  const receiveAmount = debouncedAmount
    ? orderInfo?.makerTokenAmount
      ? formatUnits(orderInfo.makerTokenAmount, token.decimals)
      : undefined
    : '0'
  const gasFee = orderInfo?.gasFeeTokenAmount
    ? formatEther(orderInfo.gasFeeTokenAmount)
    : undefined

  const gasFeeUSD =
    gasFee && ethPrice
      ? new BigNumber(ethPrice).multipliedBy(gasFee).toFixed(2)
      : undefined

  return {
    tokenlonRate: rate,
    tokenlonReceiveAmount: receiveAmount,
    tokenlonGasFeeUSD: gasFeeUSD,
    isValidating: isValidating,
  }
}
